import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Lock, Sparkles, Loader2, Eye, EyeOff, CalendarCheck, ArrowRight, ShieldCheck } from 'lucide-react';

/**
 * 2-step beta onboarding flow:
 *   Step 1 — Set password (uses existing /api/auth/set-password)
 *   Step 2 — Book onboarding call (SkyeAI iframe) — optional-skippable
 */
export default function BetaOnboardingPage({ variant = 'beta' }) {
  const isTrial = variant === 'trial';
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, setToken, api } = useAuth();

  const token = params.get('token') || '';
  const email = params.get('email') || '';

  const [step, setStep] = useState(1);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const iframeLoadedRef = useRef(false);

  useEffect(() => {
    if (!token || !email) {
      setError(isTrial
        ? 'Invalid trial link. Please use the link from your checkout confirmation.'
        : 'Invalid beta invite link. Please use the link from your email.');
    }
  }, [token, email]);

  /* If the user already finished step 1 (their JWT is in localStorage and
     matches the URL email), skip the password form. Without this, a page
     refresh / browser-back / iframe-triggered reload sends them back to
     "Set your password" — except their reset_token has already been
     consumed, so re-submitting fails with "Invalid invite link" and they
     get locked out. (This is the exact bug Sarah hit.) */
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const storedToken = (typeof window !== 'undefined') ? localStorage.getItem('token') : null;
      if (!storedToken) return;
      try {
        const res = await api.get('/api/auth/me');
        const me = res?.data;
        if (cancelled) return;
        if (me?.email && email && me.email.toLowerCase() === email.toLowerCase()) {
          if (setUser) setUser(me);
          setStep(2);
        }
      } catch (_e) {
        // Stale/expired token — ignore, leave on step 1
      }
    };
    run();
    return () => { cancelled = true; };
  }, [api, email, setUser]);

  // Load SkyeAI iframe embed script once we're on step 2
  useEffect(() => {
    if (step !== 2 || iframeLoadedRef.current) return;
    iframeLoadedRef.current = true;
    const s = document.createElement('script');
    s.src = 'https://links.skyeai.io/js/form_embed.js';
    s.async = true;
    document.body.appendChild(s);
  }, [step]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Password must be at least 8 characters');
    if (password !== confirm) return setError('Passwords do not match');

    setLoading(true);
    try {
      const res = await api.post('/api/auth/set-password', { token, email, password });
      const { token: jwt, user } = res.data;
      localStorage.setItem('token', jwt);
      // Persist the beta workspace so Step 2's "Enter my workspace" navigates correctly
      const wsId = user?.active_workspace_id || user?.workspace_id;
      if (wsId) localStorage.setItem('currentWorkspaceId', wsId);
      if (setToken) setToken(jwt);
      if (setUser) setUser(user);
      toast.success(isTrial ? 'Password set. Your 7-day trial is live!' : 'Password set. Welcome to the beta!');
      setStep(2);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to set password';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const finish = () => {
    const ws = (typeof window !== 'undefined') ? localStorage.getItem('currentWorkspaceId') : null;
    if (ws) navigate(`/workspace/${ws}`, { replace: true });
    else navigate('/', { replace: true });
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: '#0a0b0f' }}
      data-testid="beta-onboarding-page"
    >
      <div className="w-full max-w-3xl">
        {/* Aluminum BETA badge header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 border border-white/20"
               style={{ background: isTrial ? 'linear-gradient(135deg,#5eead4,#2dd4bf)' : 'linear-gradient(135deg,#cbd5e1,#94a3b8)' }}>
            <Sparkles size={14} className="text-[#0a0b0f]" />
            <span className="text-[#0a0b0f] text-xs font-extrabold tracking-[0.25em]">
              {isTrial ? '7-DAY TRIAL · FULL ACCESS' : 'BETA · VIP ACCESS'}
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-semibold text-white">
            {isTrial ? 'Welcome to Voxinity AI' : 'Welcome to Voxinity AI Beta'}
          </h1>
          <p className="text-sm text-slate-400 mt-2">
            Step <span className="text-teal-300 font-medium">{step}</span> of 2 · {email || 'your account'}
          </p>

          {/* Progress */}
          <div className="mt-5 flex items-center justify-center gap-2">
            <div className={`h-1.5 w-24 rounded-full ${step >= 1 ? 'bg-teal-400' : 'bg-white/10'}`} />
            <div className={`h-1.5 w-24 rounded-full ${step >= 2 ? 'bg-teal-400' : 'bg-white/10'}`} />
          </div>
        </div>

        {step === 1 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur p-8" data-testid="beta-onboarding-step-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-teal-400/10 border border-teal-400/20 flex items-center justify-center">
                <ShieldCheck className="text-teal-400" size={20} />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Set your password</h2>
                <p className="text-sm text-slate-400">{isTrial ? 'Used to sign in to your workspace.' : 'Used to sign in to your Beta workspace.'}</p>
              </div>
            </div>

            <form onSubmit={submitPassword} className="space-y-4" data-testid="beta-set-password-form">
              <div>
                <label className="block text-sm text-slate-300 mb-1.5">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                  <input
                    type={show ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-10 py-2.5 rounded-lg bg-[#0f1116] border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-400/60"
                    placeholder="At least 8 characters"
                    required
                    minLength={8}
                    data-testid="beta-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    tabIndex={-1}
                  >
                    {show ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-300 mb-1.5">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                  <input
                    type={show ? 'text' : 'password'}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-[#0f1116] border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-teal-400/60"
                    placeholder="Re-enter password"
                    required
                    minLength={8}
                    data-testid="beta-password-confirm"
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-lg border border-rose-400/30 bg-rose-400/10 p-3 text-sm text-rose-300" data-testid="beta-set-password-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !token || !email}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-teal-400 text-[#0a0b0f] font-semibold hover:bg-teal-500 transition-colors disabled:opacity-50"
                data-testid="beta-submit-password-btn"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                {loading ? 'Setting password…' : 'Continue to Onboarding Call'}
              </button>
            </form>

            <p className="text-xs text-slate-500 mt-5 text-center">
              You'll book a quick onboarding call with our team on the next step.
            </p>
          </div>
        )}

        {step === 2 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur p-6 sm:p-8" data-testid="beta-onboarding-step-2">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-phosphor-400/10 border border-purple-400/20 flex items-center justify-center">
                  <CalendarCheck className="text-purple-300" size={20} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-white">Book your onboarding call</h2>
                  <p className="text-sm text-slate-400">15-min strategy call with the Voxinity team. Highly recommended.</p>
                </div>
              </div>
              <button
                onClick={finish}
                className="text-xs text-slate-400 hover:text-white px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5"
                data-testid="beta-skip-call-btn"
              >
                Skip for now
              </button>
            </div>

            <div className="rounded-xl overflow-hidden border border-white/10 bg-white">
              <iframe
                src="https://links.skyeai.io/widget/booking/UTSmNKx10xCIHjDQ6Zfe"
                style={{ width: '100%', border: 'none', overflow: 'hidden', minHeight: 650 }}
                scrolling="no"
                id="UTSmNKx10xCIHjDQ6Zfe_1776648455431"
                title="Voxinity onboarding call"
                data-testid="beta-skyeai-iframe"
              />
            </div>

            <button
              onClick={finish}
              className="mt-5 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-teal-400 text-[#0a0b0f] font-semibold hover:bg-teal-500 transition-colors"
              data-testid="beta-finish-onboarding-btn"
            >
              Enter my workspace
              <ArrowRight size={18} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
